import { forwardRef, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useEquipment } from '@/features/equipment/queries';
import { cn } from '@/lib/utils';

interface EquipmentPickerProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  /** Vessel scope. The picker fetches /equipment?vesselId=… and is
   *  disabled when no vessel is selected. */
  vesselId: string | null;
  value: string | null;
  onChange: (equipmentId: string | null) => void;
  /** Hide inactive equipment from the list (default: true). */
  hideInactive?: boolean;
}

/**
 * EquipmentPicker — vessel-scoped equipment dropdown for the
 * Work Orders form. Different concern from ParentEquipmentPicker:
 * here we pick THE target of a Work Order, not a parent for a
 * hierarchy reparenting. Both happen to share the same backing
 * query, so we don't introduce a second hook.
 *
 * Native <select> styled like the rest of the form inputs, with
 * a leading "—" placeholder option to force an explicit choice.
 * Disabled until a vesselId is set, which the form clears whenever
 * the user changes vessel (a target equipment from another vessel
 * would fail backend validation).
 */
export const EquipmentPicker = forwardRef<
  HTMLSelectElement,
  EquipmentPickerProps
>(
  (
    { vesselId, value, onChange, hideInactive = true, className, ...rest },
    ref,
  ) => {
    const { t } = useTranslation();
    const { data, isLoading } = useEquipment({
      vesselId: vesselId ?? undefined,
    });

    const items = (data ?? []).filter((e) => !hideInactive || e.isActive);

    return (
      <select
        ref={ref}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        disabled={!vesselId || isLoading || rest.disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        <option value="">{t('common.notSpecified', { defaultValue: '—' })}</option>
        {items.map((e) => (
          <option key={e.id} value={e.id}>
            {e.tag} — {e.name}
          </option>
        ))}
      </select>
    );
  },
);
EquipmentPicker.displayName = 'EquipmentPicker';
